import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../core/config/api";
import TopUpCreditsModal from "../../components/TopUpCreditsModal";
// Pagination styles shared with the other customer pages (Credit Logs, Email Verifier).
import "./email_verify.css";
import "./credit_billing.css";

// Credits & Billing (customer panel): the live balance, top-up ("PAYG")
// packs bought this period, a warning before they lapse, and every credit
// movement. Data: GET /stripe-payment/credit-billing.

interface Pack {
    payment_id: number;
    credits_purchased: number;
    amount: number;
    currency: string;
    used: number;
    remaining: number;
    purchased_on: string | null;
    expires_on: string | null;
    days_left: number | null;
}

interface ActivityRow {
    id: number;
    date: string | null;
    activity: string;
    credit_type: string;
    credits: number;
}

interface BillingResponse {
    balance: {
        available: number;
        allocated: number;
        bonus: number;
        used: number;
        plan_name: string | null;
        billing_interval: string | null;
        period_end: string | null;
        days_left: number | null;
    };
    payg: { remaining: number; active_packages: number };
    packs: Pack[];
    expiry_warning: {
        credits: number;
        days_left: number;
        expires_on: string | null;
        oldest_pack_credits: number | null;
        oldest_purchased_on: string | null;
    } | null;
    activity: { items: ActivityRow[]; total: number; page: number; page_size: number; total_pages: number };
}

const PAGE_SIZE = 10;
const fmt = (n: number) => Number(n || 0).toLocaleString("en-US");
const fmtDate = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
const fmtMoney = (amount: number, currency = "USD") =>
    new Intl.NumberFormat((currency || "USD").toUpperCase() === "INR" ? "en-IN" : "en-US", {
        style: "currency",
        currency: currency || "USD",
        minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    }).format(amount);

const CreditBilling = () => {
    const navigate = useNavigate();
    const [data, setData] = useState<BillingResponse | null>(null);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showTopUp, setShowTopUp] = useState(false);
    const [goToPageInput, setGoToPageInput] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: body } = await apiClient.get("/stripe-payment/credit-billing", {
                params: { page, page_size: PAGE_SIZE },
            });
            setData(body);
            setError(null);
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === "string" && detail !== "Not Found" ? detail : "Unable to load your credits. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [page]);

    useEffect(() => {
        load();
    }, [load]);

    const warning = data?.expiry_warning;
    const activity = data?.activity;
    const totalPages = activity?.total_pages || 1;

    const handlePageChange = (next: number) => {
        if (next >= 1 && next <= totalPages) setPage(next);
    };

    const handleGoToPage = () => {
        const next = parseInt(goToPageInput, 10);
        if (!isNaN(next) && next >= 1 && next <= totalPages) handlePageChange(next);
    };

    // Same windowing as the other pages: 1 2 3 ... N
    const pageNumbers = (): (number | string)[] => {
        if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
        if (page <= 3) return [1, 2, 3, "...", totalPages];
        if (page >= totalPages - 2) return [1, "...", totalPages - 2, totalPages - 1, totalPages];
        return [1, "...", page, "...", totalPages];
    };

    return (
        <div className="credit-billing-page">
            <div className="cb-header">
                <h1 className="cb-title">Credits &amp; Billing</h1>
                <div className="cb-balance-pill">
                    <span className="cb-balance-value">{data ? fmt(data.balance.available) : "—"}</span>
                    <span className="cb-balance-label">Credits</span>
                    <button type="button" className="cb-buy-btn" onClick={() => setShowTopUp(true)}>
                        <span aria-hidden="true">+</span> Buy Credits
                    </button>
                </div>
            </div>

            {error && !data ? (
                <div className="cb-card cb-placeholder">
                    <span>{error}</span>
                    <button type="button" className="cb-link" onClick={load}>Retry</button>
                </div>
            ) : (
                <div className="cb-layout">
                    <div className="cb-main">
                        {warning && (
                            <div className="cb-warning" role="status">
                                <div>
                                    <p className="cb-warning-title">
                                        {fmt(warning.credits)} top-up credits expire with your plan renewal
                                        {warning.days_left === 0 ? " today" : ` in ${warning.days_left} day${warning.days_left === 1 ? "" : "s"}`}.
                                    </p>
                                    <p className="cb-warning-text">
                                        Run a verification before {fmtDate(warning.expires_on)} or they'll lapse.
                                    </p>
                                </div>
                                <button type="button" className="cb-primary-btn" onClick={() => navigate("/email-verifier?tab=bulk")}>
                                    Verify a List
                                </button>
                            </div>
                        )}

                        {data && data.packs.length > 0 && (
                            <section className="cb-card">
                                <h2 className="cb-card-title">Active credit packs</h2>
                                <p className="cb-card-sub">
                                    Packs are consumed oldest-expiry first, after your
                                    {data.balance.billing_interval === "annual" || data.balance.billing_interval === "yearly"
                                        ? " annual"
                                        : data.balance.billing_interval === "monthly"
                                            ? " monthly"
                                            : ""}{" "}
                                    subscription credits run out.
                                </p>
                                <div className="cb-table-wrap">
                                    <table className="cb-table">
                                        <thead>
                                            <tr>
                                                <th>Credits Purchased</th>
                                                <th>Amount</th>
                                                <th>Used</th>
                                                <th>Remaining</th>
                                                <th>Purchased On</th>
                                                <th>Expires On</th>
                                                <th>Expires In</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.packs.map((pack) => (
                                                <tr key={pack.payment_id}>
                                                    <td>{fmt(pack.credits_purchased)}</td>
                                                    <td>{fmtMoney(pack.amount, pack.currency)}</td>
                                                    <td>{fmt(pack.used)}</td>
                                                    <td>{fmt(pack.remaining)}</td>
                                                    <td>{fmtDate(pack.purchased_on)}</td>
                                                    <td>{fmtDate(pack.expires_on)}</td>
                                                    <td className={pack.days_left !== null && pack.days_left <= 10 ? "cb-soon" : "cb-ok"}>
                                                        {pack.days_left === null ? "—" : `${pack.days_left} day${pack.days_left === 1 ? "" : "s"} left`}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                        )}

                        <section className="cb-card">
                            <h2 className="cb-card-title">Credit Activity</h2>
                            <p className="cb-card-sub">Track how your credits were purchased and used.</p>
                            <div className="cb-table-wrap">
                                <table className="cb-table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Activity</th>
                                            <th>Credit Type</th>
                                            <th className="cb-num">Credits</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading && !activity ? (
                                            <tr><td colSpan={4} className="cb-empty">Loading...</td></tr>
                                        ) : !activity || activity.items.length === 0 ? (
                                            <tr><td colSpan={4} className="cb-empty">No credit activity yet.</td></tr>
                                        ) : (
                                            activity.items.map((row) => (
                                                <tr key={row.id}>
                                                    <td>{fmtDate(row.date)}</td>
                                                    <td>{row.activity}</td>
                                                    <td><span className={`cb-type cb-type-${row.credit_type.toLowerCase()}`}>{row.credit_type}</span></td>
                                                    <td className={`cb-num ${row.credits < 0 ? "cb-minus" : "cb-plus"}`}>
                                                        {row.credits > 0 ? "+" : row.credits < 0 ? "−" : ""}{fmt(Math.abs(row.credits))}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {activity && activity.total_pages > 1 && (
                                <div className="pagination-container cb-pagination">
                                    <button className="pagination-arrow" onClick={() => handlePageChange(page - 1)} disabled={page === 1}>
                                        &lt;
                                    </button>
                                    {pageNumbers().map((pageNum, index) =>
                                        pageNum === "..." ? (
                                            <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
                                        ) : (
                                            <button
                                                key={pageNum}
                                                className={`pagination-number ${page === pageNum ? "active" : ""}`}
                                                onClick={() => handlePageChange(pageNum as number)}
                                            >
                                                {pageNum}
                                            </button>
                                        )
                                    )}
                                    <button className="pagination-arrow" onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>
                                        &gt;
                                    </button>
                                    <div className="go-to-page">
                                        <span>Go to page</span>
                                        <input
                                            type="number"
                                            min="1"
                                            max={totalPages}
                                            value={goToPageInput}
                                            onChange={(e) => setGoToPageInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") handleGoToPage();
                                            }}
                                            onBlur={handleGoToPage}
                                            className="page-input"
                                            aria-label="Go to page"
                                        />
                                    </div>
                                </div>
                            )}
                        </section>
                    </div>

                    <aside className="cb-card cb-summary">
                        <div className="cb-stat">
                            <p className="cb-stat-label">Total available</p>
                            <p className="cb-stat-value">{data ? fmt(data.balance.available) : "—"}</p>
                            <p className="cb-stat-hint">Across subscription + top-up</p>
                        </div>
                        <div className="cb-stat">
                            <p className="cb-stat-label">Subscription credits</p>
                            <p className="cb-stat-value cb-accent">
                                {data ? fmt(Math.max(0, data.balance.available - data.payg.remaining)) : "—"}
                                {data?.balance.plan_name && <span className="cb-stat-inline">{data.balance.plan_name} plan</span>}
                            </p>
                            <p className="cb-stat-hint">
                                {data?.balance.period_end ? `Renews ${fmtDate(data.balance.period_end)}` : "Renews with your plan"}
                            </p>
                        </div>
                        <div className="cb-stat">
                            <p className="cb-stat-label">Top-up credits</p>
                            <p className="cb-stat-value cb-green">
                                {data ? fmt(data.payg.remaining) : "—"}
                                {data && (
                                    <span className="cb-stat-inline">
                                        {data.payg.active_packages} active top-up{data.payg.active_packages === 1 ? "" : "s"}
                                    </span>
                                )}
                            </p>
                            <p className="cb-stat-hint">Expire with renewal</p>
                        </div>
                    </aside>
                </div>
            )}

            {showTopUp && (
                <TopUpCreditsModal
                    onClose={() => {
                        setShowTopUp(false);
                        load();
                    }}
                />
            )}
        </div>
    );
};

export default CreditBilling;
